import { BaseRHFWidgetProps } from "@impero-com/common/components/form/rhfFields/types";
import { assertNever } from "@impero-com/common/utils/obj";
import { Divider } from "@impero-com/ui";
import { Control, FieldValues, useWatch } from "react-hook-form";

import { ControlConclusionAnswerKind } from "api/generated/model";
import type { SavedBlob } from "api/generated/model";
import { Guid } from "utils/guid";

import { HeadingTask } from "./HeadingTask";
import { TaskField } from "./TaskField";
import { TaskWrapper } from "./TaskWrapper";
import { HasTasks, InternalName, TasksField, TasksFields } from "./types";
import { getVisibleFields } from "./utils";

interface TasksProps<
    TFieldValues extends FieldValues,
> extends BaseRHFWidgetProps<TFieldValues, Record<Guid, unknown>> {
    fields: TasksFields;
    savedBlobs: SavedBlob[];
}
export function Tasks<TFieldValues extends FieldValues>({
    fields,
    control,
    name,
    savedBlobs,
}: TasksProps<TFieldValues>) {
    const internalControl = control as FieldValues as Control<HasTasks>;
    const internalName = name as InternalName;

    const fieldsById = useWatch({
        control: internalControl,
        name: internalName,
    });
    const maybeGetFieldValue: (
        id: string
    ) => ControlConclusionAnswerKind["value"] | undefined = (id: Guid) =>
        fieldsById?.[id];
    const visibleFields = getVisibleFields(fields, maybeGetFieldValue);
    // Needs to be optimized - only conditional fields (fields that can be hidden) should watch the value of the field which they depend on
    // Currently form rerenders on any keystroke

    return (
        <>
            {visibleFields.flatMap((field, idx) => {
                const reactField = (
                    <Field
                        key={name + field.id}
                        field={field}
                        internalName={internalName}
                        internalControl={internalControl}
                        savedBlobs={savedBlobs}
                    />
                );
                return idx === 0
                    ? reactField
                    : [<Divider key={`divider-${field.id}`} />, reactField];
            })}
        </>
    );
}

interface FieldProps {
    field: TasksField;
    internalName: InternalName;
    internalControl: Control<HasTasks>;
    savedBlobs: SavedBlob[];
}
function Field({
    field,
    internalName,
    internalControl,
    savedBlobs,
}: FieldProps) {
    switch (field.fieldType.type) {
        case "CheckboxGroup":
        case "Dropdown":
        case "NumericInput":
        case "FileUpload":
        case "RadioButtonGroup":
        case "TextArea":
        case "TextInput":
        case "Effectiveness":
            return (
                <TaskWrapper field={field}>
                    <TaskField
                        field={field}
                        control={internalControl}
                        name={internalName}
                        savedBlobs={savedBlobs}
                    />
                </TaskWrapper>
            );
        case "Heading":
            return <HeadingTask field={field} />;
        default:
            return assertNever(field.fieldType);
    }
}
